import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDebounce } from 'app/utils/hooks/useDebounce';
import { DEFAULT_PAGE } from 'app/utils/pagination';
import { useSearchParams } from 'app/utils/searchParams';

export const useReviewsFilters = () => {
  const { getSearchParam, replaceSearchParams } = useSearchParams();

  const [searchInput, setSearchInput] = useState(
    getSearchParam('search') || ''
  );
  const debouncedSearch = useDebounce(searchInput, 500);

  const pageNumber = getSearchParam('pageNumber');
  const beginning = getSearchParam('beginning');
  const ending = getSearchParam('ending');
  const sortingColumn = getSearchParam('sortingColumn');
  const sortingOrder = getSearchParam('sortingOrder');

  const filters = useMemo(
    () => ({
      pageNumber: Number(pageNumber) || DEFAULT_PAGE,
      search: debouncedSearch || undefined,
      beginning: beginning || undefined,
      ending: ending || undefined,
      sortingColumn: sortingColumn || undefined,
      sortingOrder: sortingOrder as 'ASC' | 'DESC' | undefined,
    }),
    [
      pageNumber,
      debouncedSearch,
      beginning,
      ending,
      sortingColumn,
      sortingOrder,
    ]
  );

  const updateFilters = useCallback(
    (updates: Partial<typeof filters>) => {
      const allParams: Record<string, string> = {};

      (
        [
          'pageNumber',
          'search',
          'beginning',
          'ending',
          'sortingColumn',
          'sortingOrder',
        ] as const
      ).forEach((key) => {
        const value = getSearchParam(key);

        if (value) {
          allParams[key] = value;
        }
      });

      if (!('pageNumber' in updates) && Object.keys(updates).length > 0) {
        allParams.pageNumber = '1';
      }

      const columnMap: Record<string, string> = {
        createdAt: 'CREATED_AT',
        globalScore: 'GLOBAL_SCORE',
      };

      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === '' || value === null) {
          delete allParams[key];
        } else {
          allParams[key] =
            key === 'sortingColumn'
              ? (columnMap[value] ?? String(value))
              : String(value);
        }
      });

      replaceSearchParams(allParams);
    },
    [getSearchParam, replaceSearchParams]
  );

  const handleDateChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
    (beginning?: string, ending?: string) => {
      updateFilters({ beginning, ending });
    },
    [updateFilters]
  );

  useEffect(() => {
    const currentSearch = getSearchParam('search') || '';
    const newSearch = debouncedSearch || '';

    if (newSearch !== currentSearch) {
      updateFilters({ search: debouncedSearch || undefined });
    }
  }, [debouncedSearch, getSearchParam, updateFilters]);

  return {
    filters,
    searchInput,
    setSearchInput,
    updateFilters,
    handleDateChange,
  };
};
